import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Skeleton,
  Tooltip,
  Link as MuiLink,
  alpha,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink } from "react-router-dom";
import { useGetParentInvoicesQuery } from "../../../Api/attoDeskApi";
import { filterParentInvoices, summariseParentInvoices } from "./invoiceHistoryFilters";
import { StudentProfileLink } from "../../../Components/ProfileLinks";
import useUser from "../../../Core/hook/useUser";
import { openInvoicePdf } from "../../Finance/Invoices/printHelpers";
import { useNotifier } from "../../../Core/Notifier";

const money = (n: any) => {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
};

const fmtDate = (d?: string | null) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return String(d).slice(0, 10);
  }
};

const StatusBadge: React.FC<{ status?: string | null; isOverdue?: boolean }> = ({ status, isOverdue }) => {
  const s = (status || "").toLowerCase();
  const label = isOverdue && s !== "paid" ? "Overdue" : s.charAt(0).toUpperCase() + s.slice(1) || "—";
  const [bg, fg] = (() => {
    if (isOverdue && s !== "paid") return ["#fee2e2", "#991b1b"];
    if (s === "paid") return ["#dcfce7", "#166534"];
    if (s === "partial") return ["#fef3c7", "#92400e"];
    if (s === "sent") return ["#dbeafe", "#1d4ed8"];
    if (s === "pending" || s === "draft") return ["#f1f5f9", "#475569"];
    if (s === "voided" || s === "cancelled") return ["#e2e8f0", "#334155"];
    return ["#f1f5f9", "#475569"];
  })();
  return (
    <Chip
      size="small"
      label={label}
      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: bg, color: fg, textTransform: "capitalize" }}
    />
  );
};

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tint: string;
}> = ({ icon, label, value, tint }) => (
  <Box
    sx={{
      borderRadius: "16px",
      p: 2,
      background: `linear-gradient(135deg, ${alpha(tint, 0.14)} 0%, ${alpha(tint, 0.04)} 100%)`,
      border: `1px solid ${alpha(tint, 0.24)}`,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      minHeight: 84,
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(tint, 0.2),
        color: tint,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.disabled" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary", lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const InvoiceRow: React.FC<{
  inv: any;
  studentCanClick: boolean;
  canPay: boolean;
  onOpenView: (id: number) => void;
  onPrint: (id: number, invoiceNumber?: string) => void;
  onPdf: (id: number, invoiceNumber?: string) => void;
  busyAction: null | "print" | "pdf";
}> = ({ inv, canPay, onOpenView, onPrint, onPdf, busyAction }) => {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(inv.items) ? inv.items : [];
  const hasBalance = Number(inv.balance_due || 0) > 0;
  const overdue = !!inv.is_overdue;
  return (
    <>
      <TableRow sx={{ "& td": { borderBottom: "1px solid rgba(15,23,42,0.06)" } }}>
        <TableCell sx={{ width: 42 }}>
          {items.length > 0 && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <MuiLink
            component={RouterLink}
            to={`/finance/invoices/${inv.id}`}
            underline="hover"
            sx={{ fontSize: 13, fontWeight: 800, color: "primary.main" }}
          >
            {inv.invoice_number || `#${inv.id}`}
          </MuiLink>
        </TableCell>
        <TableCell>
          {inv.student_id ? (
            <StudentProfileLink id={inv.student_id} name={inv.student_name} sx={{ fontSize: 12.5 }}>
              {inv.student_name || "Student"}
            </StudentProfileLink>
          ) : (
            <Typography sx={{ fontSize: 12.5 }}>{inv.student_name || "—"}</Typography>
          )}
        </TableCell>
        <TableCell><Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{inv.billing_period || "—"}</Typography></TableCell>
        <TableCell><Typography sx={{ fontSize: 12.5 }}>{fmtDate(inv.issue_date)}</Typography></TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5, color: overdue ? "#b91c1c" : "text.primary", fontWeight: overdue ? 700 : 500 }}>
            {fmtDate(inv.due_date)}
          </Typography>
        </TableCell>
        <TableCell align="right"><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{money(inv.total_amount)}</Typography></TableCell>
        <TableCell align="right"><Typography sx={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>{money(inv.paid_amount)}</Typography></TableCell>
        <TableCell align="right">
          <Typography sx={{ fontSize: 13, color: hasBalance ? "#b45309" : "text.disabled", fontWeight: 700 }}>
            {money(inv.balance_due)}
          </Typography>
        </TableCell>
        <TableCell><StatusBadge status={inv.status} isOverdue={overdue} /></TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="View Invoice" arrow>
              <IconButton size="small" onClick={() => onOpenView(inv.id)} aria-label="View Invoice">
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Invoice" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPrint(inv.id, inv.invoice_number)}
                  disabled={busyAction !== null}
                  aria-label="Print Invoice"
                >
                  <PrintOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download PDF" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPdf(inv.id, inv.invoice_number)}
                  disabled={busyAction !== null}
                  aria-label="Download PDF"
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {canPay && hasBalance && (
              <Tooltip title="Record Payment" arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onOpenView(inv.id)}
                  sx={{ textTransform: "none", fontSize: 11.5, py: 0.25, borderRadius: "8px", ml: 0.25 }}
                >
                  Pay
                </Button>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      {items.length > 0 && (
        <TableRow>
          <TableCell colSpan={11} sx={{ p: 0, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ px: 3, py: 2, bgcolor: "#f8fafc" }}>
                <Stack spacing={1}>
                  {items.map((it: any, idx: number) => {
                    const mode = String(it.teaching_mode || "").toLowerCase();
                    const modeLabel = mode === "online" ? "Online" : (mode === "offline" || mode === "in_person" || mode === "inperson") ? "In Person" : mode === "hybrid" ? "Hybrid" : null;
                    const chip = [it.location_name, modeLabel].filter(Boolean).join(" • ");
                    // Normalise every backend variation to "Monthly Fee" or
                    // "Per Class". Quantity is displayed as small secondary
                    // text on per-class rows only.
                    const btToken = String(it.billing_type || it.itemType || "").toLowerCase().replace(/[\s_-]/g, "");
                    const isPerClass = btToken.includes("perclass");
                    const feeTypeLabel = isPerClass ? "Per Class" : "Monthly Fee";
                    const qtyNumber = Number(it.quantity || 0);
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: { xs: "flex-start", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 1,
                          p: 1.25,
                          borderRadius: "10px",
                          border: "1px solid rgba(15,23,42,0.06)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "-0.01em" }}>
                            {it.subject_name || it.description || "Line item"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
                            {[
                              it.grade_name || null,
                              it.staff_name ? `Staff: ${it.staff_name}` : null,
                            ].filter(Boolean).join(" • ")}
                          </Typography>
                          {(chip || it.billing_period_label) && (
                            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}>
                              {[chip, it.billing_period_label ? `${it.billing_period_label} ${isPerClass ? "Per Class" : "Monthly"} Fee` : null].filter(Boolean).join(" • ")}
                            </Typography>
                          )}
                        </Box>
                        <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.25} sx={{ ml: { xs: 0, sm: 2 }, minWidth: 96 }}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary" }}>
                            {feeTypeLabel}
                            {isPerClass && qtyNumber > 0 ? ` · ${qtyNumber} ${qtyNumber === 1 ? "class" : "classes"}` : ""}
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                            {money(it.amount)}
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const InvoiceHistorySection: React.FC<{ parentId: string; childrenList: any[] }> = ({ parentId, childrenList }) => {
  const theme = useTheme();
  const { canView, canPay: canPayHook } = useUser() as any;
  const { showErrorMessage } = useNotifier();
  const canViewInvoices = canView ? canView("Invoice") : true;
  const canPay = canPayHook ? canPayHook("Invoice") : false;

  const { data, isLoading, isError, refetch } = useGetParentInvoicesQuery(parentId, {
    skip: !parentId || !canViewInvoices,
  });

  const [studentFilter, setStudentFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [search, setSearch] = useState("");

  const summary = (data as any)?.data?.summary ?? {
    total_invoiced: 0,
    total_paid: 0,
    outstanding: 0,
    overdue: 0,
    invoice_count: 0,
  };
  const invoices: any[] = (data as any)?.data?.invoices ?? [];

  const filtered = useMemo(
    () => filterParentInvoices(invoices, { studentFilter, statusFilter, outstandingOnly, search }),
    [invoices, studentFilter, statusFilter, outstandingOnly, search],
  );

  // Client-side recompute so summary cards reflect current filters.
  const filteredSummary = useMemo(() => summariseParentInvoices(filtered), [filtered]);
  const shownSummary = studentFilter || statusFilter || outstandingOnly || search ? filteredSummary : summary;

  const goToInvoice = (id: number) => {
    window.location.href = `/finance/invoices/${id}`;
  };
  // Track which invoice/action is currently in-flight so we can disable the
  // action buttons and prevent duplicate tabs/downloads on rapid clicks.
  const [busy, setBusy] = useState<{ id: number; kind: "print" | "pdf" } | null>(null);
  const onPrint = async (id: number, invoiceNumber?: string) => {
    if (busy) return;
    setBusy({ id, kind: "print" });
    try { await openInvoicePdf(id, invoiceNumber); }
    catch (e: any) { showErrorMessage(e?.message || "Failed to open Print"); }
    finally { setBusy(null); }
  };
  const onPdf = async (id: number, invoiceNumber?: string) => {
    if (busy) return;
    setBusy({ id, kind: "pdf" });
    try { await openInvoicePdf(id, invoiceNumber, "download"); }
    catch (e: any) { showErrorMessage(e?.message || "Failed to open PDF"); }
    finally { setBusy(null); }
  };

  if (!canViewInvoices) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 1.5 }}>Invoice History</Typography>
        <Box
          sx={{
            borderRadius: "16px",
            border: "1px solid rgba(15,23,42,0.08)",
            bgcolor: "#fff",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            You don't have permission to view invoice information for this parent.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Invoice History</Typography>
        <Chip
          size="small"
          label={`${shownSummary.invoice_count} invoice${shownSummary.invoice_count === 1 ? "" : "s"}`}
          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
        />
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard icon={<ReceiptLongOutlinedIcon />} label="Total Invoiced" value={money(shownSummary.total_invoiced)} tint="#3b82f6" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard icon={<PaidOutlinedIcon />} label="Total Paid" value={money(shownSummary.total_paid)} tint="#22c55e" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard icon={<AccountBalanceWalletOutlinedIcon />} label="Outstanding" value={money(shownSummary.outstanding)} tint="#f59e0b" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard icon={<WarningAmberOutlinedIcon />} label="Overdue" value={money(shownSummary.overdue)} tint="#ef4444" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard icon={<FormatListNumberedIcon />} label="Invoices" value={shownSummary.invoice_count} tint="#8b5cf6" />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box
        sx={{
          borderRadius: "12px",
          border: "1px solid rgba(15,23,42,0.06)",
          bgcolor: "#fff",
          p: 1.5,
          mb: 1.5,
          display: "flex",
          gap: 1.25,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          select
          value={studentFilter}
          onChange={(e) => setStudentFilter(e.target.value)}
          sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { fontSize: 12.5, borderRadius: "9px" } }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All Students</MenuItem>
          {childrenList.map((c: any) => (
            <MenuItem key={c.student_id} value={String(c.student_id)}>{c.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140, "& .MuiOutlinedInput-root": { fontSize: 12.5, borderRadius: "9px" } }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="partial">Partial</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
        </TextField>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search invoice #"
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 0.5 }} /> }}
          sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { fontSize: 12.5, borderRadius: "9px" } }}
        />
        <Button
          size="small"
          variant={outstandingOnly ? "contained" : "outlined"}
          onClick={() => setOutstandingOnly((v) => !v)}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "9px", fontSize: 12 }}
        >
          Outstanding Only
        </Button>
      </Box>

      {/* Table / states */}
      <Box sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", bgcolor: "#fff", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3, color: "#475569", bgcolor: "#f8fafc" } }}>
                <TableCell />
                <TableCell>Invoice #</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && [0, 1, 2].map((i) => (
                <TableRow key={i}>{Array.from({ length: 11 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))}
              {isError && !isLoading && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#b91c1c", mb: 1 }}>
                        Failed to load invoice history.
                      </Typography>
                      <Button size="small" variant="outlined" onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "8px" }}>
                        Retry
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>
                        No invoices found for this parent.
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.5 }}>
                        Invoices generated for linked students will appear here.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && filtered.map((inv) => (
                <InvoiceRow
                  key={inv.id}
                  inv={inv}
                  studentCanClick={!!inv.student_id}
                  canPay={canPay}
                  onOpenView={goToInvoice}
                  onPrint={onPrint}
                  onPdf={onPdf}
                  busyAction={busy && busy.id === inv.id ? busy.kind : null}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default InvoiceHistorySection;
